import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Channel } from '@meradio/shared-types';
import { useAppDispatch, useAppSelector } from '@/store/store';
import { followChannel, unfollowChannel, selectIsChannelFollowed } from '../channelsSlice';
import { Button, YouTubeChannelLink } from '@/components/ui';
import ChannelPreview from '@/components/ChannelPreview';

interface ChannelCardProps {
  channel: Channel;
  onChannelClick?: (channel: Channel) => void;
  showFollowButton?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  previewEnabled?: boolean;
}

const ChannelCard: React.FC<ChannelCardProps> = ({
  channel,
  onChannelClick,
  showFollowButton = true,
  size = 'md',
  className = '',
  previewEnabled = false,
}) => {
  const dispatch = useAppDispatch();
  const isFollowed = useAppSelector(selectIsChannelFollowed(channel.channelId));
  const { isFollowing, isUnfollowing, followError } = useAppSelector(state => state.channels);
  const [expanded, setExpanded] = useState(false);

  const handleFollowClick = async (e: React.MouseEvent) => {
    e.stopPropagation();

    try {
      if (isFollowed) {
        await dispatch(unfollowChannel(channel.channelId)).unwrap();
      } else {
        await dispatch(followChannel(channel)).unwrap();
      }
    } catch (error) {
      console.error(followError || 'Something went wrong', error);
    }
  };

  const handleCardClick = () => {
    if (onChannelClick) {
      onChannelClick(channel);
    }
  };

  const sizeClasses = {
    sm: {
      container: 'p-3',
      thumbnail: 'w-12 h-12',
      title: 'text-sm',
      description: 'text-xs',
    },
    md: {
      container: 'p-4',
      thumbnail: 'w-16 h-16',
      title: 'text-base',
      description: 'text-sm',
    },
    lg: {
      container: 'p-6',
      thumbnail: 'w-20 h-20',
      title: 'text-lg',
      description: 'text-base',
    },
  };

  const currentSize = sizeClasses[size];
  const isProcessing = isFollowing || isUnfollowing;
  const buttonSize = size === 'sm' ? 'sm' : 'md';

  return (
    <div
      className={`
        bg-white dark:bg-bg-dark-secondary border border-meradio-light dark:border-border-dark rounded-xl shadow-sm
        hover:shadow-lg hover:border-meradio-primary dark:hover:border-meradio-primary hover:transform hover:scale-[1.02]
        transition-all duration-200 cursor-pointer
        ${currentSize.container}
        ${className}
      `}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      aria-label={`Channel: ${channel.name}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleCardClick();
        }
      }}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3 flex-1 min-w-0">
          <div className={`${currentSize.thumbnail} flex-shrink-0`}>
            {channel.thumbnailUrl ? (
              <img
                src={channel.thumbnailUrl}
                alt={`${channel.name} thumbnail`}
                className="w-full h-full rounded-full object-cover border-2 border-meradio-light"
                loading="lazy"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(channel.name)}&size=80&background=6C5CE7&color=ffffff&format=png&rounded=true`;
                }}
              />
            ) : (
              <div className="w-full h-full rounded-full bg-gradient-to-br from-meradio-primary to-meradio-secondary flex items-center justify-center">
                <span className="text-white font-bold text-lg">
                  {channel.name.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <h3 className={`font-semibold text-meradio-dark dark:text-text-dark-primary ${currentSize.title} truncate`}>
              {channel.name}
            </h3>
            
            {channel.description && (
              <p className={`text-meradio-dark-light dark:text-text-dark-secondary ${currentSize.description} mt-1 line-clamp-2`}>
                {channel.description}
              </p>
            )}

            {isFollowed && (
              <Link
                to={`/channels/${channel.channelId}`}
                onClick={(e) => e.stopPropagation()}
                className="inline-block mt-2 text-xs font-medium text-meradio-primary hover:text-meradio-primary-dark"
              >
                View channel
              </Link>
            )}
          </div>
        </div>

        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          <div className="flex items-center gap-2">
            {showFollowButton && (
              <Button
                onClick={handleFollowClick}
                disabled={isProcessing}
                isLoading={isProcessing}
                variant={isFollowed ? 'secondary' : 'primary'}
                size={buttonSize}
                aria-label={isFollowed ? `Unfollow ${channel.name}` : `Follow ${channel.name}`}
              >
                {isProcessing
                  ? isFollowed ? 'Unfollowing...' : 'Following...'
                  : isFollowed ? '✓' : '+ Follow'}
              </Button>
            )}

            <YouTubeChannelLink
              channelId={channel.channelId}
              name={channel.name}
              className="text-meradio-dark-light dark:text-text-dark-secondary"
            />
          </div>

          {previewEnabled && (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                setExpanded((prev) => !prev);
              }}
              variant="outline"
              size="sm"
              aria-label={expanded ? `Hide preview for ${channel.name}` : `Preview ${channel.name}`}
              aria-expanded={expanded}
              className="!px-2"
            >
              {expanded ? (
                <>
                  Hide
                  <ChevronUp className="w-3.5 h-3.5" />
                </>
              ) : (
                <>
                  Preview
                  <ChevronDown className="w-3.5 h-3.5" />
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {previewEnabled && expanded && (
        <div
          className="mt-3 pt-3 border-t border-meradio-light dark:border-border-dark"
          onClick={(e) => e.stopPropagation()}
        >
          <ChannelPreview channelId={channel.channelId} channelName={channel.name} enabled={expanded} />
        </div>
      )}
    </div>
  );
};

export default ChannelCard;
